import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Grid,
  IconButton,
  Divider,
  Box,
  Stack,
  Button,
  TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HistoryIcon from '@mui/icons-material/History';
import { RootState } from '../types/store';
import { setSelectedDriver } from '../store/slices/driverSlice';
import { ref, update, get, set } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import AddPhoneDialog from '../components/AddPhoneDialog';
import LinkedPhonesList from '../components/LinkedPhonesList';
import AccountSyncHistoryComponent from '../components/AccountSyncHistory';
import { accountSyncService } from '../services/accountSyncService';
import { expireDateSyncService } from '../services/expireDateSyncService';
import { GroupMemberInfo, PhoneSearchResult, AccountSyncHistory } from '../types/accountSync';
import { UI_TEXTS, PERMISSION_CONFIG } from '../config/accountSyncConfig';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';
// import { useAuth } from '../context/AuthContext'; // 삭제

const DriverDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedDriver = useSelector((state: RootState) => state.driver.selectedDriver);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [expireDate, setExpireDate] = useState(selectedDriver?.expiredate || '');
  const [refereal, setRefereal] = useState(selectedDriver?.referealcode || '');
  const [username, setUsername] = useState(selectedDriver?.username || '');
  
  // 추가폰 관련 상태
  const [addPhoneDialogOpen, setAddPhoneDialogOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState<GroupMemberInfo[]>([]);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState<string | undefined>();
  
  // 권한 체크
  const isSuperMaster = currentUser?.grade === 'supermaster';
  const isLocalMaster = currentUser?.grade === 'localmaster';
  const canEditRefereal = isSuperMaster || isLocalMaster; // 추천인 수정 권한
  const canEditExpireDate = isSuperMaster; // 기간 연장 권한
  const canManageLinkedPhones = isSuperMaster; // 추가폰 관리 권한
  // const { currentUser } = useAuth(); // 삭제

  useEffect(() => {
    if (!id) return;
    console.log('🔄 DriverDetailPage - Fetching driver with ID:', id);
    const fetchDriver = async () => {
      const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${id}`);
      const snapshot = await get(driverRef);
      if (snapshot.exists()) {
        const driverData = snapshot.val();
        console.log('📱 Driver data fetched:', {
          id,
          phonenumber: driverData.phonenumber,
          username: driverData.username
        });
        dispatch(setSelectedDriver({ id, ...driverData }));
        
        // 추가폰 그룹 정보 로드
        if (canManageLinkedPhones && driverData.phonenumber) {
          console.log('👥 Loading group members for phone:', driverData.phonenumber);
          loadGroupMembers(driverData.phonenumber);
        } else {
          console.log('⚠️ Not loading group members:', {
            canManageLinkedPhones,
            phonenumber: driverData.phonenumber
          });
        }
      } else {
        console.error('❌ Driver not found with ID:', id);
        dispatch(setSelectedDriver(null));
      }
    };
    fetchDriver();
  }, [id, dispatch, canManageLinkedPhones]);

  // 그룹 멤버 로드
  const loadGroupMembers = async (phoneNumber: string) => {
    setLoadingGroup(true);
    console.log('🔍 Loading group members for:', phoneNumber);
    try {
      const members = await accountSyncService.getGroupMembers(phoneNumber);
      console.log('✅ Group members loaded:', {
        phoneNumber,
        members,
        membersCount: members.length
      });
      setGroupMembers(members);
      
      // 그룹 ID 설정
      const groupId = await accountSyncService.getGroupId(phoneNumber);
      console.log('📋 Group ID:', groupId);
      setCurrentGroupId(groupId);
    } catch (error) {
      console.error('❌ Failed to load group members:', error);
      setGroupMembers([]);
    } finally {
      setLoadingGroup(false);
    }
  };

  // 이력 로드 함수
  const handleLoadHistory = async (groupId?: string, phoneNumber?: string): Promise<AccountSyncHistory[]> => {
    try {
      if (groupId) {
        return await accountSyncService.getHistoryByGroup(groupId);
      } else if (phoneNumber) {
        return await accountSyncService.getHistoryByPhone(phoneNumber);
      } else {
        return await accountSyncService.getAllHistory(50);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      throw error;
    }
  };

  // 전화번호 검색
  const handleSearchPhones = async (query: string): Promise<PhoneSearchResult[]> => {
    try {
      return await accountSyncService.searchDriversByPhone(query);
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  };

  // 추가폰 등록
  const handleAddPhones = async (phones: string[]) => {
    if (!selectedDriver) return;
    
    try {
      await accountSyncService.addToGroup(
        selectedDriver.phonenumber,
        phones,
        currentUser?.id || 'unknown'
      );
      
      // 그룹 멤버 다시 로드
      await loadGroupMembers(selectedDriver.phonenumber);
      setAddPhoneDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to add phones:', error);
      throw new Error(error.message || '추가폰 등록에 실패했습니다.');
    }
  };

  // 추가폰 제거
  const handleRemovePhone = async (phoneToRemove: string) => {
    if (!selectedDriver) return;
    
    try {
      await accountSyncService.removeFromGroup(
        selectedDriver.phonenumber,
        phoneToRemove,
        currentUser?.id || 'unknown'
      );
      
      // 그룹 멤버 다시 로드
      await loadGroupMembers(selectedDriver.phonenumber);
    } catch (error: any) {
      console.error('Failed to remove phone:', error);
      throw new Error(error.message || '추가폰 제거에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (selectedDriver) {
      setExpireDate(selectedDriver.expiredate);
      setRefereal(selectedDriver.referealcode);
      setUsername(selectedDriver.username);
    }
  }, [selectedDriver]);

  // 날짜 계산 함수
  const setExpire = (type: 'month' | 'plus' | 'minus') => {
    let baseDate: Date;
    const now = new Date();
    now.setHours(12, 0, 0, 0);

    // expireDate를 항상 ISO 포맷으로 변환
    const toDate = (str: string) => {
      if (!str) return new Date(now); // 빈 값이면 오늘
      const iso = str.trim().replace(' ', 'T');
      const date = new Date(iso);
      if (isNaN(date.getTime())) return new Date(now); // Invalid Date면 오늘
      return date;
    };

    if (type === 'month') {
      const expire = toDate(expireDate);
      expire.setHours(12, 0, 0, 0);
      if (expire < now) {
        baseDate = new Date(now);
      } else {
        baseDate = new Date(expire);
      }
      baseDate.setMonth(baseDate.getMonth() + 1);
      baseDate.setHours(12, 0, 0, 0);
      const y = baseDate.getFullYear();
      const m = String(baseDate.getMonth() + 1).padStart(2, '0');
      const d = String(baseDate.getDate()).padStart(2, '0');
      setExpireDate(`${y}-${m}-${d} 12:00:00`);
      return;
    }
    if (!expireDate) return;
    const date = toDate(expireDate);
    if (type === 'plus') date.setDate(date.getDate() + 1);
    if (type === 'minus') date.setDate(date.getDate() - 1);
    date.setHours(12, 0, 0, 0);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    setExpireDate(`${y}-${m}-${d} 12:00:00`);
  };

  const handleApply = async () => {
    if (!selectedDriver) return;
    const driverRef = ref(database, `${DB_PATHS.DRIVERS}/${selectedDriver.id}`);
    
    // 변경사항 확인 (권한에 따라)
    const isUsernameChanged = username !== selectedDriver.username;
    const isExpireDateChanged = canEditExpireDate && expireDate !== selectedDriver.expiredate;
    const isReferealChanged = canEditRefereal && refereal !== selectedDriver.referealcode;

    console.log('현재 값들:', {
      currentExpireDate: expireDate,
      originalExpireDate: selectedDriver.expiredate,
      currentRefereal: refereal,
      originalRefereal: selectedDriver.referealcode,
      currentUsername: username,
      originalUsername: selectedDriver.username,
      isExpireDateChanged,
      isReferealChanged,
      isUsernameChanged
    });

    // 로그용 timestamp 생성
    const now = new Date();
    const timestamp =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    
    // 로그 데이터 생성
    const baseLogData = {
      driverId: selectedDriver.id,
      driverName: selectedDriver.username,
      changedByName: currentUser?.username || 'unknown',
      changedById: currentUser?.id || 'unknown',
      changeDate: now.toISOString(),
    };

    // 변경 유형에 따른 로그 데이터 생성
    let logData;
    if (isExpireDateChanged && isReferealChanged && isUsernameChanged) {
      logData = {
        ...baseLogData,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: expireDate,
        oldReferealCode: selectedDriver.referealcode,
        newReferealCode: refereal,
        oldUsername: selectedDriver.username,
        newUsername: username,
        changeType: 'expire_date_refereal_and_username_update'
      };
      console.log('세 값 모두 변경됨:', logData);
    } else if (isExpireDateChanged && isReferealChanged) {
      logData = {
        ...baseLogData,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: expireDate,
        oldReferealCode: selectedDriver.referealcode,
        newReferealCode: refereal,
        changeType: 'expire_date_and_refereal_update'
      };
      console.log('유효기간과 추천인 변경됨:', logData);
    } else if (isExpireDateChanged && isUsernameChanged) {
      logData = {
        ...baseLogData,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: expireDate,
        oldUsername: selectedDriver.username,
        newUsername: username,
        changeType: 'expire_date_and_username_update'
      };
      console.log('유효기간과 이름 변경됨:', logData);
    } else if (isReferealChanged && isUsernameChanged) {
      logData = {
        ...baseLogData,
        oldReferealCode: selectedDriver.referealcode,
        newReferealCode: refereal,
        oldUsername: selectedDriver.username,
        newUsername: username,
        changeType: 'refereal_and_username_update'
      };
      console.log('추천인과 이름 변경됨:', logData);
    } else if (isExpireDateChanged) {
      logData = {
        ...baseLogData,
        oldExpireDate: selectedDriver.expiredate,
        newExpireDate: expireDate,
        changeType: 'expire_date_update'
      };
      console.log('유효기간만 변경됨:', logData);
    } else if (isReferealChanged) {
      logData = {
        ...baseLogData,
        oldReferealCode: selectedDriver.referealcode,
        newReferealCode: refereal,
        changeType: 'refereal_update'
      };
      console.log('추천인만 변경됨:', logData);
    } else if (isUsernameChanged) {
      logData = {
        ...baseLogData,
        oldUsername: selectedDriver.username,
        newUsername: username,
        changeType: 'username_update'
      };
      console.log('이름만 변경됨:', logData);
    }

    if (logData) {
      console.log('저장될 로그 데이터:', logData);
      // 로그 저장
      const logRef = ref(database, `${DB_PATHS.DRIVERLOGS}/${selectedDriver.id}/${timestamp}`);
      try {
        await set(logRef, logData);
        console.log('로그 저장 성공');
      } catch (error) {
        console.error('로그 저장 실패:', error);
      }
    } else {
      console.log('변경된 내용이 없어 로그를 저장하지 않음');
    }
    
    // 드라이버 데이터 업데이트 (권한에 따라)
    const updateData: any = { username: username };
    
    // 추천인 수정 권한이 있는 경우에만 추천인 업데이트
    if (canEditRefereal) {
      updateData.referealcode = refereal;
    }
    
    // 기간 수정 권한이 있는 경우에만 만료일 업데이트
    if (canEditExpireDate) {
      updateData.expiredate = expireDate;
    }
    
    try {
      await update(driverRef, updateData);
      console.log('드라이버 데이터 업데이트 성공');
      
      // 만료일이 변경된 경우 그룹 동기화 실행
      if (isExpireDateChanged && canEditExpireDate) {
        try {
          await expireDateSyncService.handleExpireDateChange(
            selectedDriver.id,
            expireDate,
            selectedDriver.expiredate,
            currentUser?.id || 'unknown'
          );
          console.log('그룹 만료일 동기화 완료');
        } catch (syncError) {
          console.error('만료일 동기화 실패:', syncError);
          // 동기화 실패는 경고만 표시하고 계속 진행
        }
      }
    } catch (error) {
      console.error('드라이버 데이터 업데이트 실패:', error);
      return;
    }
    
    // update 후 driver 정보 다시 fetch
    try {
      const snapshot = await get(driverRef);
      if (snapshot.exists()) {
        const driverData = snapshot.val();
        dispatch(setSelectedDriver({ id: selectedDriver.id, ...driverData }));
        console.log('새로운 드라이버 데이터:', driverData);
      }
    } catch (error) {
      console.error('드라이버 데이터 fetch 실패:', error);
    }

    if (currentUser?.grade === 'supermaster') {
      alert('기사 정보가 변경 적용되었습니다.');
    } else {
      alert('기사 정보가 변경 적용되었습니다.');
    }

    // 처리 완료 후 이전 페이지(기사 목록)로 이동
    navigate(-1);
  };

  if (!selectedDriver) {
    return (
      <Container>
        <Typography>기사 정보를 찾을 수 없습니다.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} edge="start">
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1 }}>
            기사 상세 정보
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" color="text.secondary">
              기본 정보
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    이름
                  </Typography>
                  <TextField
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    전화번호
                  </Typography>
                  <Typography variant="body1">{formatPhoneNumber(selectedDriver.phonenumber)}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" color="text.secondary">
              앱 정보
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    앱 이름
                  </Typography>
                  <Typography variant="body1">{selectedDriver.appname}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    매크로 앱
                  </Typography>
                  <Typography variant="body1">{selectedDriver.macroapp}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    기기 모델
                  </Typography>
                  <Typography variant="body1">{selectedDriver.phonemodel}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary">
              날짜 정보
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    로그인 날짜
                  </Typography>
                  <Typography variant="body1">{selectedDriver.logindate}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    만료 날짜
                  </Typography>
                  <Typography variant="body1">{expireDate}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {canEditRefereal ? (
              <TextField
                label="추천인"
                value={refereal}
                onChange={e => setRefereal(e.target.value)}
                fullWidth
                sx={{ mt: 2, maxWidth: 300 }}
              />
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  추천인
                </Typography>
                <Typography variant="body1">{refereal}</Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {canEditExpireDate && (
          <Stack direction="column" spacing={2} sx={{ mt: 4, mb: 2 }}>
            <Button variant="outlined" onClick={() => setExpire('month')}>1개월 연장</Button>
            <Button variant="outlined" onClick={() => setExpire('plus')}>+ 1일</Button>
            <Button variant="outlined" onClick={() => setExpire('minus')}>- 1일</Button>
          </Stack>
        )}
        
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleApply}>적용</Button>
        </Box>

        {/* 추가폰 관리 섹션 - SuperMaster만 표시 */}
        {canManageLinkedPhones && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">추가폰 관리</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setShowHistory(!showHistory)}
                >
                  {showHistory ? '이력 숨기기' : '이력 보기'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GroupAddIcon />}
                  onClick={() => setAddPhoneDialogOpen(true)}
                >
                  {UI_TEXTS.BUTTON_ADD_PHONE}
                </Button>
              </Box>
            </Box>

            {/* 추가폰 목록 */}
            <LinkedPhonesList
              groupMembers={groupMembers}
              currentPhone={selectedDriver.phonenumber}
              isSuperMaster={isSuperMaster}
              onRemove={handleRemovePhone}
              loading={loadingGroup}
            />

            {/* 이력 표시 */}
            {showHistory && currentGroupId && (
              <Box sx={{ mt: 3 }}>
                <AccountSyncHistoryComponent
                  groupId={currentGroupId}
                  phoneNumber={selectedDriver.phonenumber}
                  onLoadHistory={handleLoadHistory}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 추가폰 등록 다이얼로그 */}
      {canManageLinkedPhones && (
        <AddPhoneDialog
          open={addPhoneDialogOpen}
          onClose={() => setAddPhoneDialogOpen(false)}
          currentPhone={selectedDriver?.phonenumber || ''}
          onAdd={handleAddPhones}
          onSearch={handleSearchPhones}
        />
      )}
    </Container>
  );
};

export default DriverDetailPage; 